// Homepage REXY chat reconciler (turn-binding fix).
//
// The homepage poll previously TAIL-APPENDED server messages deduped by `sender + first-50-chars`,
// gated by a message COUNT, under a key that could differ from the optimistic-send key. That
// re-served prior turns, doubled responses, and orphaned the fresh reply (the "re-emit previous /
// double voice / ignores new input" trio).
//
// This reconciler CONVERGES the thread to the server's ORDERED list (the authority), keeping only
// the in-flight optimistic tail (opt_* messages the server hasn't persisted yet), deduped by
// NORMALIZED FULL content — not sender+first-50-chars, not a count gate, no tail re-inject.
//
// Pure + side-effect-free → node-testable (rexyChatReconcile.test.mjs) and imported by App.tsx.

function rexyMsgIdentity(sender, text) {
  // whitespace-normalized full content — robust where the optimistic copy and the server-stored
  // copy differ only by whitespace (the REXY path stores the same raw render both sides).
  return sender + '' + String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
}

function reconcileRexyMessages(serverMessages, currentList) {
  const server = (serverMessages || []).map(function (m, i) {
    return {
      id: 'srv_' + i,                                   // positional id within the server-ordered list
      sender: m.role === 'user' ? 'user' : 'rexy',
      text: m.content,
      timestamp: '',
      source: m.source || 'LOCAL_AI',
      // frontier lanes (Phase 4): a frontier turn's images, from the history row
      ...(m.images ? { images: m.images } : {}),
    };
  });
  const serverIds = new Set(server.map(function (m) { return rexyMsgIdentity(m.sender, m.text); }));
  const cur = currentList || [];
  // keep ONLY in-flight optimistic messages the server has not yet persisted (the live turn)
  const pendingOptimistic = cur.filter(function (m) {
    return m && typeof m.id === 'string' && m.id.indexOf('opt_') === 0 &&
      !serverIds.has(rexyMsgIdentity(m.sender, m.text));
  });
  const welcomes = cur.filter(function (m) {
    return m && typeof m.id === 'string' && m.id.indexOf('welcome_') === 0;
  });
  // RECITE-WIRING-DELTA — SYSTEM notices (sys_*: interface-voiced errors/statuses,
  // never persisted server-side, never attributed to Rexy) are client-only state:
  // keep them across polls exactly like welcomes, or the 10s poll would erase an
  // error notice moments after it appeared.
  const systemNotices = cur.filter(function (m) {
    return m && typeof m.id === 'string' && m.id.indexOf('sys_') === 0;
  });
  return server.concat(pendingOptimistic, systemNotices, welcomes);
}

// Pick the existing ACTIVE thread to bind a send to. Returns its id, or null when the server
// reports no active thread (caller creates one then). This is the decision that keeps a
// send-before-loadRexyThreads from fragmenting the conversation by always creating a new thread.
function resolveActiveThreadId(threads) {
  const active = (threads || []).find(function (t) { return t && t.status === 'active'; });
  return active ? active.id : null;
}

module.exports = { reconcileRexyMessages, rexyMsgIdentity, resolveActiveThreadId };
